<template>
  <div class="main-collection__scroll-container critical-component-clear">
    <div
      class="main-collection__header"
      :class="{ 'full-width-title': fullWidthTitle }"
    >
      <div class="main-collection__title-wrapper">
        <a
          class="main-collection__title-logo"
          :href="$variable('routes.root')"
        >
          <icon-brand-logo aria-hidden="true" />

          <span
            class="visually-hidden"
            v-text="$string('collection.back_to_homepage')"
          />
        </a>

        <h1
          id="template-title"
          class="main-collection__title"
          :class="{
            'visually-hidden': disableCollectionTitle,
          }"
          v-html="collectionTitle"
        />
      </div>

      <template v-if="showSubcollectionTabs">
        <p
          id="main-collection-subcollection-instructions"
          class="visually-hidden"
          v-text="$string('collection.subcollections.instructions')"
        />

        <div
          class="main-collection__subcollection-tabs-wrapper"
          role="region"
          :aria-label="$string('collection.subcollections.tabs_label')"
        >
          <ul
            class="main-collection__subcollection-tabs"
            aria-describedby="main-collection-subcollection-instructions"
            :aria-label="$string('collection.subcollections.filter_title')"
          >
            <li>
              <btn
                :aria-current="allSubcollectionsActive"
                class="main-collection__subcollection-tab-button"
                :modifiers="allSubcollectionsActive ? 'primary' : 'secondary'"
                :label="$string('collection.subcollections.reset_tabs')"
                type="button"
                @keydown.enter.prevent="resetSubcollectionTabs"
                @click.prevent="resetSubcollectionTabs"
              />
            </li>

            <li
              v-for="(subcollection, subcollectionIndex) of subcollections"
              :key="subcollectionIndex"
            >
              <btn
                :aria-current="isTabActive(subcollection.handle)"
                class="main-collection__subcollection-tab-button"
                :label="formatTitle(subcollection.title)"
                :modifiers="
                  isTabActive(subcollection.handle) ? 'primary' : 'secondary'
                "
                type="button"
                @keydown.enter.prevent="
                  toggleSubcollection(subcollectionIndex, subcollection.handle)
                "
                @click.prevent="
                  toggleSubcollection(subcollectionIndex, subcollection.handle)
                "
              />
            </li>
          </ul>
        </div>
      </template>

      <filter-and-sort
        v-if="collectionData.totalProducts > 0"
        :filters="filtersData"
        :filter-visibility="collectionData && collectionData?.filterVisibility"
        :current-sort="sortBy"
        :sort-options="sortOptions"
        :product-count="totalProductsPreview"
        @update-products="updateProducts"
        @update-drawer-criteria="updateProducts"
      />
    </div>

    <search-bar
      v-if="searchPage"
      :query="collectionData.query"
      :product-count="totalProducts"
    />

    <div
      v-if="showSubcollections"
      class="main-collection__subcollections"
    >
      <template
        v-for="(subcollection, subcollectionIndex) of activeSubcollections"
        :key="subcollectionIndex"
      >
        <div
          class="main-collection__subcollection"
          :class="{
            grid: renderCard(subcollection),
            'has-card': renderCard(subcollection),
          }"
        >
          <h2
            class="main-collection__subcollection-card-title text-heading-l"
          >
            <a
              :href="subcollection.url"
              v-text="formatTitle(subcollection.title)"
            />
          </h2>
          <div
            v-if="renderCard(subcollection)"
            class="main-collection__subcollection-card"
            :class="{ 'col xs4 l1-3': renderCard(subcollection) }"
          >
            <div class="main-collection__subcollection-card-inner">
              <responsive-image
                v-if="subcollection.image.src"
                :class="subcollection.subcollectionImageClass"
                :image="subcollection.image"
                min-max="200-500"
                ratio="231:274"
              />

              <div
                v-if="subcollection.description"
                class="
                  text-body-m-regular
                  main-collection__subcollection-card-description
                "
                v-html="subcollection.description"
              />

              <btn
                v-if="subcollectionSettings.cardButton"
                class="main-collection__subcollection-card-button"
                :url="subcollection.url"
                modifiers="secondary center"
                :label="$string('collection.subcollections.card_button_label')"
              />
            </div>
          </div>

          <div
            class="main-collection__grid grid js-collection-grid"
            :class="{
              'has-card col xs4 m12 l4-12': renderCard(subcollection),
              'limit-products': subcollectionSettings.limitProducts,
            }"
          >
            <template
              v-for="(item, index) of getSubcollectionProducts(subcollection)"
              :key="item.id"
            >
              <product-card
                v-if="item.price?.amount || item.price?.amount === 0"
                ref="product-card"
                :class="gridCols"
                :product="item"
                :lazyload="subcollectionIndex !== 0 || index > 4"
                :denim-card-copy="denimCardCopy"
                :show-btn-discount="showBtnDiscount"
              />

              <in-grid-banner
                v-else-if="item?.isBanner && showInGridBanners"
                class="col"
                :card-image-height="cardImageHeight"
                :class="item.gridClass"
                :banner="item.content"
                :lazyload="index > 4"
              />
            </template>

            <btn
              v-if="subcollectionSettings.limitProducts"
              class="
                main-collection__subcollection-product-grid-button
                col
                xs-span
                l-span
              "
              modifiers="secondary center"
              :label="
                $string('collection.subcollections.product_grid_button_label')
              "
              type="button"
              :url="subcollection.url"
            />
          </div>
          <hr v-if="subcollectionIndex < activeSubcollections.length - 1">
        </div>
      </template>
    </div>

    <div
      v-else-if="productList.length"
      class="main-collection__grid grid js-collection-grid"
    >
      <template
        v-for="(item, index) of filteredProducts"
        :key="item.id"
      >
        <product-card
          v-if="item.price?.amount || item.price?.amount === 0"
          ref="product-card"
          :class="gridCols"
          :product="item"
          :lazyload="index > 4"
          :show-btn-discount="showBtnDiscount"
        />

        <in-grid-banner
          v-else-if="item?.isBanner && showInGridBanners"
          class="col"
          :card-image-height="cardImageHeight"
          :class="item.gridClass"
          :banner="item.content"
          :lazyload="index > 4"
        />
      </template>
    </div>

    <p
      v-if="showNoResultsMessage"
      class="main-collection__empty text-body-l-regular text-heading-m-desktop"
      v-text="$string('collection.no_results')"
    />

    <main-pagination
      v-if="usePagination"
      :current-page="currentPage"
      :pagination="pagination"
      :pagination-type="paginationType"
      :current-products-count="productList.length"
      :total-products-count="totalProducts"
      :show-pagination-buttons="showPaginationButtons"
      :pagination-is-loading="paginationIsLoading"
      @load-more="handleLoadMore"
      @view-all="handleViewAll"
    />
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection.
 *
 * @param {Object} collectionData - Formatted Liquid collection object.
 * @param {Boolean} denimCardCopy - Whether to use denim product metafields.
 * @param {Object} filters - Formatted Liquid filters object.
 * @param {Object} sortOptions - Formatted Liquid sort options object.
 * @param {Array} subcollections - Formatted Liquid subcollection objects.
 * @param {Object} subcollectionSettings - Section settings for subcollections.
 * @param {Boolean} sixCardGrid - Whether to use 6 card rows on desktop.
 * @param {Boolean} disableCollectionTitle - disable the collection title.
 */
import { mapState } from 'vuex'
import { fetchJsonView } from '~/helpers/general'
import ProductCard from '~async/product-card/product-card'
import Btn from '~global/btn/btn.vue'
import ResponsiveImage from '~global/images/responsive-image'
import IconBrandLogo from '~icons/general/brand-logo.svg'
import FilterAndSort from './filter-and-sort/filter-and-sort'
import InGridBanner from './in-grid-banner/in-grid-banner'
import MainPagination from './main-pagination'
import SearchBar from './search-bar/search-bar'

export default {
  name: 'MainCollection',

  components: {
    Btn,
    IconBrandLogo,
    FilterAndSort,
    InGridBanner,
    SearchBar,
    MainPagination,
    ProductCard,
    ResponsiveImage,
  },

  props: {
    collectionData: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    denimCardCopy: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Object,
      default: () => ({}),
    },
    subcollections: {
      type: Array,
      default: () => [],
    },
    subcollectionSettings: {
      type: Object,
      default: () => ({
        cardButton: false,
        limitProducts: false,
        tabs: false,
      }),
    },
    sixCardGrid: {
      type: Boolean,
      default: false,
    },
    disableCollectionTitle: {
      type: Boolean,
      default: false,
    },
    paginationType: {
      type: String,
      default: 'pagination',
    },
    showBtnDiscount: {
      type: String,
      default: 'show',
    },
  },

  data() {
    return {
      activeSubcollections: this.subcollections,
      cardImageHeight: 0,
      currentPage: null,
      filtersApplied: false,
      filtersData: this.filters,
      pageType: window.cnvs.page.type,
      pagination: this.collectionData.pageInfo,
      showPaginationButtons: true,
      isLoadingMore: false,
      productList: this.collectionData.products,
      sortBy: this.sortOptions.current,
      totalProducts: this.collectionData.totalProducts,
      totalProductsPreview: this.collectionData.totalProducts,
      perPage: this.$variable('settings.pagination.products'),
      totalPages: 0,
      paginationIsLoading: false,
    }
  },

  computed: {

    /**
     * Filters the product list to exclude disabled products and include only
     * available products,
     * bannered products (if showInGridBanners is true),
     * and products tagged with 'Preorder:'.
     *
     * @returns {Array} The filtered list of products.
     */

    filteredProducts() {
      return this.productList?.filter((product) => {
        const actualAvailableProducts = product?.available
        const actualBanneredProducts =
          product?.isBanner && this.showInGridBanners
        const actualPreOrderTaggedProducts = product?.tags?.some((tag) => tag?.includes('Preorder:'))
        return (
          actualAvailableProducts ||
          actualBanneredProducts ||
          actualPreOrderTaggedProducts
        )
      })
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      width: (state) => state.index.screen.width,
    }),

    /**
     * Build accessible collection title.
     * @returns {String}
     */
    collectionTitle() {
      return `
        <span class="visually-hidden">
          ${this.$string('collection.brand')}
        </span>

        ${this.collectionData.title}
      `
    },

    /**
     * Whether this is a search page.
     * @returns {Boolean}
     */
    searchPage() {
      return this.pageType === 'search'
    },

    /**
     * Whether to show subcollections, based on merchandising
     * and active filter/sort options.
     * @returns {Boolean}
     */
    showSubcollections() {
      if (!this.subcollections.length) {
        return false
      }

      return !this.filtersApplied && this.currentPage === 1
    },

    /**
     * Whether to show in-grid banners.
     * @returns {Boolean}
     */
    showInGridBanners() {
      return this.currentPage === 1 && !this.filtersApplied
    },

    /**
     * Whether to show subcollections tabs.
     * @returns {Boolean}
     */
    showSubcollectionTabs() {
      return (
        this.showSubcollections &&
        this.subcollectionSettings.tabs &&
        !this.filtersApplied
      )
    },

    /**
     * Whether all subcollections are currently active.
     */
    allSubcollectionsActive() {
      return this.activeSubcollections.length === this.subcollections.length
    },

    /**
     * Whether to show 'no results' message.
     * @returns {Boolean}
     */
    showNoResultsMessage() {
      return this.productList.length === 0 && !this.searchPage
    },

    /**
     * Whether to render page title and full-width.
     * @returns {Boolean}
     */
    fullWidthTitle() {
      return (
        (this.showSubcollections && this.subcollectionSettings.tabs) ||
        this.filtersApplied
      )
    },

    /**
     * Compute whether to use pagination
     * @returns {Boolean}
     */
    usePagination() {
      if (this.showSubcollections) {
        return false
      }

      return this.pagination.pages > 1
    },

    /**
   * Dynamically set grid column classes based on sixCardGrid prop.
   */
    gridCols() {
      // 6-card grid
      if (this.sixCardGrid) {
        return 'col xs2 m6 l2'
      }
      // 4-card grid
      return 'col xs2 m6 l3'
    },
  },

  watch: {

    /**
     * On window resize, update cardImageHeight to resize in-grid banners.
     */
    width() {
      this.cardImageHeight = this.getCardImageHeight()
    },
  },

  mounted() {
    this.getCurrentPage()
    this.redirectIfEmptyPage()
    this.checkFilterParams()
    this.getAvailableFiltersData()
    sessionStorage.setItem('cameFromPLP', 'true')
    sessionStorage.setItem('plpUrl', window.location.href)
    sessionStorage.setItem('plpCollectionName', this.collectionData.title)
    if (this.currentPage > 1 && this.paginationType !== 'pagination') {
      this.loadCumulativeProducts()
    }

    if (this.pageType === 'collection') {
      this.formatAllBanners()
      this.cardImageHeight = this.getCardImageHeight()
      this.getTotalAvailableCount()
    }
  },

  methods: {
    redirectIfEmptyPage() {
      if (
        this.currentPage > 1 &&
        (!this.productList || this.productList.length === 0)
      ) {
        const params = new URLSearchParams(window.location.search)
        params.set('page', '1')

        const newUrl = `${window.location.pathname}?${params.toString()}`
        window.location.href = newUrl
      }
    },

    async loadCumulativeProducts() {
      const promises = []
      for (let page = 2; page <= this.currentPage; page++) {
        const params = new URLSearchParams(window.location.search)
        params.set('page', page)
        const queryUrl = `${
          window.location.pathname
        }?${params.toString()}&view=json&type=product`
        promises.push(fetchJsonView(queryUrl))
      }

      try {
        const results = await Promise.all(promises)
        results.forEach((data) => {
          this.productList.push(...data.products)
        })
        if (this.currentPage > 1) {
          this.$nextTick().then(() => {
            const targetIndex = this.perPage * (this.currentPage - 1)
            const cards = Array.isArray(this.$refs['product-card'])
              ? this.$refs['product-card']
              : [this.$refs['product-card']]
            if (cards[targetIndex]) {
              cards[targetIndex].$el.scrollIntoView({ behavior: 'smooth' })
            }
          })
        }
      } catch (error) {
        cnvs.ReportError('Failed to load cumulative products:', error)
      }
    },

    /**
     * Get the total count of available products (available or tagged with
     * "Preorder:"), recursively fetching pages if needed.
     * The result is cached based on the collection handle.
     */
    async getTotalAvailableCount() {
      if (this.filtersApplied) {
        return
      }

      const perPage = this.perPage || 32
      this.paginationIsLoading = true

      // Use collection handle as the cache key.
      const storageKey = `totalCount-${this.collectionData.handle}`
      let totalCount = sessionStorage.getItem(storageKey)

      if (totalCount === null) {
        // No cached value; recursively fetch all pages.
        const fetchPage = async(page, runningTotal) => {
          let total = runningTotal
          const url = `${window.location.origin}${window.location.pathname}?page=${page}`
          const fullUrl = `${url}&view=json&type=product`

          try {
            const data = await fetchJsonView(fullUrl)
            const currentProducts = data.products || []

            // Count only products that are available or "Preorder:".
            const filteredProducts = currentProducts.filter(
              (product) => product.available ||
                (product.tags &&
                  product.tags.some((tag) => tag.includes('Preorder:'))),
            )
            const currentCount = filteredProducts.length
            total += currentCount

            // If the page is full, recursively fetch the next page.
            if (currentProducts.length >= perPage) {
              return await fetchPage(page + 1, total)
            }
            return total
          } catch (error) {
            cnvs.ReportError('Error fetching products on page', page, error)
            return total
          }
        }

        totalCount = await fetchPage(1, 0)
        sessionStorage.setItem(storageKey, totalCount)
      } else {
        totalCount = Number(totalCount)
      }
      this.totalProducts = totalCount

      // Update total pages based on the available count.
      this.totalPages = Math.ceil(totalCount / perPage)
      if (this.pagination) {
        this.pagination.pages = this.totalPages
      }

      this.paginationIsLoading = false
    },

    /**
     * Load more products.
     */
    async handleLoadMore() {
      try {
        this.currentPage += 1

        const params = new URLSearchParams(window.location.search)
        params.set('page', this.currentPage)

        let queryUrl = `${window.location.pathname}?${params.toString()}`
        queryUrl += '&view=json&type=product'

        const data = await fetchJsonView(queryUrl)

        this.productList.push(...data.products)

        this.pagination = data.pageInfo
      } catch (error) {
        cnvs.ReportError('Failed to load more products:', error)
      }
    },

    /**
     * View all products(infinite scroll).
     */
    handleViewAll() {
      this.showPaginationButtons = false
      this.setupInfiniteScroll()
    },

    /**
     * Setup infinite scroll.
     */
    setupInfiniteScroll() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }

      const existingSentinel = document.querySelector('#scroll-sentinel')
      if (existingSentinel) {
        existingSentinel.remove()
      }

      const grid = document.querySelector('.js-collection-grid')
      if (!grid) {
        return
      }

      // Create a new sentinel at the bottom.
      const sentinel = document.createElement('div')
      sentinel.id = 'scroll-sentinel'
      sentinel.style.height = '1px'
      grid.appendChild(sentinel)

      const options = {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0.1,
      }

      // Create a new IntersectionObserver.
      this.observer = new IntersectionObserver((entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting)

        if (isIntersecting && this.pagination.hasNextPage) {
          if (this.isLoadingMore) {
            return
          }
          this.isLoadingMore = true

          this.handleLoadMore().finally(() => {
            this.isLoadingMore = false
          })
        }
      }, options)

      this.observer.observe(sentinel)
    },

    beforeDestroy() {
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    /**
     * Format all banners to render into product grid.
     */
    formatAllBanners() {
      if (!this.collectionData.inGridBanners && !this.subcollections) {
        return
      }

      this.collectionData.inGridBanners?.forEach((banner) => {
        this.formatInGridBanner(banner, this.collectionData.handle)
      })

      this.subcollections.forEach((subcollection) => {
        subcollection.inGridBanners?.forEach((banner) => {
          this.formatInGridBanner(banner, subcollection.handle)
        })
      })
    },

    /**
     * Format individual banner by adding it to its collection's product array.
     * @param {Object} banner
     * @param {String} collectionHandle
     */
    formatInGridBanner(banner, collectionHandle) {
      banner.isBanner = true
      banner.gridClass = this.getBannerGridClass(banner.content.double_width)

      if (collectionHandle === this.collectionData.handle) {
        if (this.productList[banner.position].isBanner) {
          return
        }

        this.productList.splice(banner.position, 0, banner)
        return
      }

      const subcollection = this.subcollections.find(
        (collection) => collectionHandle === collection.handle,
      )

      if (subcollection.products[banner.position].isBanner) {
        return
      }

      subcollection.products.splice(banner.position, 0, banner)
    },

    getBannerGridClass(isDoubleWidth) {
      if (this.sixCardGrid) {
        return isDoubleWidth ? 'xs4 m12 l4' : 'xs2 m6 l2'
      }

      return isDoubleWidth ? 'xs4 m12 l6' : 'xs2 m6 l3'
    },

    /**
     * Remove 'bag' or 'bags' from end of title string
     * @param {String} title - Subcollection title
     * @returns {String}
     */
    formatTitle(title) {
      const regex = new RegExp(/ bags?$/, 'i')
      return title.replace(regex, '')
    },

    /**
     * Update the filtersData prop to prevent showing options
     * that only exist on unavailable products.
     */
    async getAvailableFiltersData() {
      const params = new URLSearchParams(window.location.search)

      const keys = [...params.keys()]
      keys.forEach((key) => {
        if (!key.includes('filter') && key !== 'sort_by') {
          return
        }
        params.delete(key)
      })

      const paramString = params.toString()
      const queryString = `?${paramString}&filter.v.availability=1`
      const url = `${window.location.origin}${window.location.pathname}${queryString}`

      const data = await fetchJsonView(`${url}&view=json&type=product`)

      this.filtersData = data.filters
    },

    /**
     * Get current page
     */
    getCurrentPage() {
      const params = new URLSearchParams(window.location.search)
      const page = params.get('page') ?? '1'

      // If page wasn't set in the URL, update it.
      if (!params.get('page')) {
        params.set('page', page)
        const url = `${window.location.origin}${
          window.location.pathname
        }?${params.toString()}`
        this.updateHistory(url)
      }

      this.currentPage = Number(page)
    },

    /**
     * Get an array of subcollection products, with limit applied, if necessary.
     * @param {Array}
     * @returns {Array}
     */
    getSubcollectionProducts(subcollection) {
      const availableProducts = subcollection.products.filter(
        (product) => product.available ||
          product.tags?.some((tag) => tag.includes('Preorder:')),
      )
      if (!this.subcollectionSettings.limitProducts) {
        return availableProducts
      }

      const desktopRowWidth = this.sixCardGrid ? 6 : 4

      return availableProducts.slice(0, desktopRowWidth)
    },

    /**
     * Whether to display subcollection card
     * @param {Object} subcollection
     * @returns {Boolean}
     */
    renderCard(subcollection) {
      return subcollection.image?.src || subcollection.description
    },

    /**
     * Determine if subcollection tab is active.
     * @param {String} handle - Subcollection handle
     * @returns {Boolean}
     */
    isTabActive(handle) {
      return (
        this.activeSubcollections.length < this.subcollections.length &&
        this.activeSubcollections.some(
          (subcollection) => subcollection.handle === handle,
        )
      )
    },

    /**
     * Reset subcollection tabs by displaying all.
     */
    resetSubcollectionTabs() {
      this.activeSubcollections = this.subcollections
    },

    /**
     * Toggle subcollection visibility on tab click.
     * @param {Number} index - Subcollection index
     * @param {String} handle - Subcollection handle
     */
    toggleSubcollection(index, handle) {
      if (this.isTabActive(handle)) {
        if (this.activeSubcollections.length === 1) {
          this.activeSubcollections = this.subcollections
          return
        }

        this.activeSubcollections = this.activeSubcollections.filter(
          (subcollection) => subcollection.handle !== handle,
        )
        return
      }

      const toggledSubcollection = this.subcollections[index]
      toggledSubcollection.inGridBanners?.forEach((banner) => {
        this.formatInGridBanner(banner, handle)
      })

      if (this.activeSubcollections.length === this.subcollections.length) {
        this.activeSubcollections = [toggledSubcollection]
        return
      }

      this.activeSubcollections.push(toggledSubcollection)
      this.activeSubcollections.sort((a, b) => {
        const indexA = this.subcollections.indexOf(
          this.subcollections.find((item) => item.handle === a.handle),
        )
        const indexB = this.subcollections.indexOf(
          this.subcollections.find((item) => item.handle === b.handle),
        )

        return indexA - indexB
      })
    },

    /**
     * Update product data based on filters.
     * @param {Object} filters
     * @param {Boolean} replaceGrid - Whether to replace product grid
     * @param {Boolean} clearAll - Whether filters are all being cleared
     * @param {Boolean} drawerClosed - Whether drawer close triggered this
     */
    updateProducts(filters, replaceGrid, clearAll, drawerClosed) {
      const hasFiltersBeforeUpdate = this.filtersApplied
      this.filtersApplied = filters.length > 0

      if (drawerClosed && hasFiltersBeforeUpdate) {
        this.filtersApplied = true
        return
      }

      const params = new URLSearchParams(window.location.search)

      /**
       * Perform the query on the first page to prevent 'no results' from
       * temporarily flashing on the screen when there are actually products.
       */
      if (this.currentPage > 1) {
        params.set('page', 1)
      }

      const keys = [...params.keys()]
      keys.forEach((key) => {
        if (!key.includes('filter') && key !== 'sort_by') {
          return
        }
        params.delete(key)
      })

      filters.forEach((filter) => {
        const action = filter.param.includes('sort_by') ? 'set' : 'append'
        params[action](filter.param, filter.value)
      })

      const paramString = params.toString()
      const queryString = `?${paramString}`
      const url = `${window.location.origin}${window.location.pathname}${queryString}`

      this.fetchProducts(url, replaceGrid, clearAll)

      if (replaceGrid) {
        this.updateHistory(url)
      }
    },

    /**
     * Fetches products from JSON template
     * @param {String} url
     * @param {Boolean} replaceGrid - Whether to replace product grid
     * @param {Boolean} clearAll - Whether filters are all being cleared
     */
    async fetchProducts(url, replaceGrid, clearAll) {
      try {
        let queryUrl = url

        if (!clearAll) {
          queryUrl += '&filter.v.availability=1'
        }

        const data = await fetchJsonView(`${queryUrl}&view=json&type=product`)

        if (!replaceGrid) {
          this.totalProductsPreview = data.totalProducts
          return
        }

        this.currentPage = data.pageInfo.currentPage
        this.filtersData = data.filters
        this.pagination = data.pageInfo
        this.productList = data.products
        this.totalProducts = data.totalProducts
        this.totalProductsPreview = data.totalProducts

        if (
          clearAll &&
          this.collectionData?.inGridBanners &&
          this.subcollections?.length === 0
        ) {
          this.collectionData.inGridBanners.forEach((banner) => {
            this.formatInGridBanner(banner, this.collectionData.handle)
          })
        }
      } catch (error) {
        cnvs.ReportError('Failed to load products', error)
      }
    },

    /**
     * Update URL with filter data.
     * @param {String} url
     */
    updateHistory(url) {
      history.replaceState({ html: '' }, '', url)
    },

    /**
     * Check for filter params in URL.
     */
    checkFilterParams() {
      const paramsLength = new URLSearchParams(document.location.search).size

      if (paramsLength === 0) {
        return
      }

      if (
        document.location.search.includes('filter') ||
        document.location.search.includes('sort_by')
      ) {
        this.filtersApplied = true
      }
    },

    /**
     * Get product card height in pixels to pass to in-grid-banner(s).
     * @returns {Number}
     */
    getCardImageHeight() {
      return this.$refs['product-card'][0].$el.querySelector('.images')
        .offsetHeight
    },
  },
}
</script>

<style lang="scss">
@import "./main-collection";
</style>
